import React, { useEffect, useState, useCallback, useMemo } from 'react'
import { supabase } from '@/lib/supabase/client'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { useGestor } from '@/contexts/GestorContext'
import { toast } from 'sonner'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Wallet } from 'lucide-react'

type VerbaRow = {
  id: string
  created_at: string
  pipefy_card_id: string
  centro_custo_id: string
  tipo_despesa_id: string
  ano: number
  mes: number | null
  valor: number
  status: string
  card_titulo: string | null
  justificativa: string | null
  aprovado_em: string | null
}

export default function GestorVerbasAdicionaisPage() {
  const { areaCentroPipefyId, anoSelecionado } = useGestor()
  const [rows, setRows] = useState<VerbaRow[]>([])
  const [loading, setLoading] = useState(true)

  const fetchData = useCallback(async () => {
    if (!anoSelecionado || !areaCentroPipefyId) {
      setRows([])
      setLoading(false)
      return
    }
    setLoading(true)
    try {
      const { data, error } = await supabase
        .from('solis_verbas_adicionais')
        .select(
          'id, created_at, pipefy_card_id, centro_custo_id, tipo_despesa_id, ano, mes, valor, status, card_titulo, justificativa, aprovado_em'
        )
        .eq('centro_custo_id', areaCentroPipefyId)
        .eq('ano', parseInt(anoSelecionado, 10))
        .order('created_at', { ascending: false })

      if (error) throw error
      setRows((data as VerbaRow[]) || [])
    } catch (e) {
      console.error(e)
      toast.error('Erro ao carregar verbas adicionais.')
    } finally {
      setLoading(false)
    }
  }, [anoSelecionado, areaCentroPipefyId])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const resumo = useMemo(() => {
    const aprovado = rows.filter((r) => r.status === 'aprovado')
    const total = aprovado.reduce((s, r) => s + Number(r.valor || 0), 0)
    const qtd = aprovado.length
    const cancelados = rows.filter((r) => r.status === 'cancelado').length
    return { total, qtd, cancelados, totalLinhas: rows.length }
  }, [rows])

  const formatCurrency = (v: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v)

  const formatDate = (iso: string | null) => {
    if (!iso) return '—'
    try {
      return new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
    } catch {
      return iso
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center items-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#131E29]/20 border-t-orange-500 border-r-[#131E29]" />
      </div>
    )
  }

  if (!areaCentroPipefyId) {
    return (
      <p className="text-sm text-slate-500">
        Selecione uma área no cabeçalho para ver as verbas adicionais deste centro de custo.
      </p>
    )
  }

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="grid gap-4 sm:grid-cols-3">
        <Card className="border-none shadow-sm bg-white">
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Total aprovado ({anoSelecionado})</CardTitle>
            <Wallet className="h-4 w-4 text-orange-600" />
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-slate-900">{formatCurrency(resumo.total)}</p>
            <p className="text-xs text-slate-500 mt-1">{resumo.qtd} registro(s) aprovado(s)</p>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Registros no período</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-slate-900">{resumo.totalLinhas}</p>
            <p className="text-xs text-slate-500 mt-1">incluindo aprovados e demais situações</p>
          </CardContent>
        </Card>
        <Card className="border-none shadow-sm bg-white">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-slate-500">Cancelados</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold text-slate-900">{resumo.cancelados}</p>
            <p className="text-xs text-slate-500 mt-1">registros cancelados</p>
          </CardContent>
        </Card>
      </div>

      <Card className="border-none shadow-sm bg-white">
        <CardHeader>
          <CardTitle className="text-base">Tabela detalhada</CardTitle>
          <CardDescription>Ordenado do mais recente para o mais antigo.</CardDescription>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto max-h-[560px] overflow-y-auto">
            <Table>
              <TableHeader className="sticky top-0 bg-slate-50/95 backdrop-blur">
                <TableRow>
                  <TableHead className="pl-4">Data</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Valor</TableHead>
                  <TableHead>Mês</TableHead>
                  <TableHead>Título</TableHead>
                  <TableHead className="min-w-[200px]">Justificativa</TableHead>
                  <TableHead className="pr-4 text-xs text-slate-500">Código do processo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="text-center text-slate-500 py-14">
                      Nenhuma verba adicional encontrada para esta área e ano.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r) => (
                    <TableRow key={r.id} className="border-slate-50">
                      <TableCell className="pl-4 text-sm whitespace-nowrap">{formatDate(r.created_at)}</TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className={
                            r.status === 'aprovado'
                              ? 'border-orange-200 bg-orange-50 text-[#131E29]'
                              : 'border-slate-300 bg-slate-50 text-slate-700'
                          }
                        >
                          {r.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right text-sm font-medium">{formatCurrency(Number(r.valor))}</TableCell>
                      <TableCell className="text-sm">{r.mes ?? '—'}</TableCell>
                      <TableCell className="text-sm max-w-[200px] truncate" title={r.card_titulo || ''}>
                        {r.card_titulo || '—'}
                      </TableCell>
                      <TableCell className="text-sm text-slate-600 max-w-[280px]">
                        <span className="line-clamp-3">{r.justificativa || '—'}</span>
                      </TableCell>
                      <TableCell className="pr-4 text-xs text-slate-500 max-w-[120px] truncate" title={r.pipefy_card_id}>
                        {r.pipefy_card_id}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>
    </div>
  )
}
